/**
 * InsForge `auth.users`-native email/password staff sign-in.
 *
 * InsForge owns the password credential: the browser calls
 * `insforge.auth.signInWithPassword` (access token held in memory, refresh via the
 * SDK's httpOnly cookie). The staff `profiles` row is NOT returned by InsForge, so
 * it is resolved here via `profiles.auth_user_id = auth.users.id` and gated on
 * approved + active. SMS sign-in is a separate plane (see `staff-auth-client.ts`).
 * Identity contract: `docs/security/auth.md`.
 */

import { createClient } from "@insforge/sdk";
import { readBackendConfig } from "@/lib/backend-config";
import { getBackendClient } from "@/lib/backend";
import { insforge } from "@/lib/insforge";
import { isApprovedProfile, StaffSessionError } from "../approval";
import type { AuthProfile, StaffSessionResult } from "../types";

const activeRestores = new Set<AbortController>();
let passwordOperations = 0;

/** A superseded restore says nothing about whether the current session exists. */
export class PasswordSessionRestoreCancelledError extends Error {
  constructor() {
    super("Password session restore was superseded.");
    this.name = "PasswordSessionRestoreCancelledError";
  }
}

export function cancelPasswordSessionRestores(): void {
  for (const controller of activeRestores) controller.abort();
}

function assertCurrent(isCurrent: () => boolean): void {
  if (!isCurrent()) throw new PasswordSessionRestoreCancelledError();
}

function combineRestoreSignals(
  requestSignal: AbortSignal | null | undefined,
  restoreSignal: AbortSignal,
  cleanups: (() => void)[],
): AbortSignal {
  if (!requestSignal) return restoreSignal;
  if (typeof AbortSignal.any === "function") {
    return AbortSignal.any([requestSignal, restoreSignal]);
  }
  const linked = new AbortController();
  for (const signal of [requestSignal, restoreSignal]) {
    const abort = () => linked.abort(signal.reason);
    if (signal.aborted) {
      abort();
      break;
    }
    signal.addEventListener("abort", abort, { once: true });
    cleanups.push(() => signal.removeEventListener("abort", abort));
  }
  return linked.signal;
}

const PROFILE_COLUMNS = "id,auth_user_id,email,phone,role,full_name,approval_status,is_active";

const SIGN_IN_FAILED = "We couldn't sign you in. Check your email and password.";
const PROFILE_LOOKUP_FAILED = "We couldn't load your staff profile. Try again.";
const ACCOUNT_PENDING =
  "Your account is awaiting approval. An administrator must approve it before you can sign in.";
const ACCOUNT_REJECTED = "This account's access request was declined. Contact an administrator.";
const ACCOUNT_INACTIVE = "This account is inactive. Contact an administrator to reactivate it.";
const NO_STAFF_PROFILE = "This account isn't set up for staff access.";
const SESSION_REFRESH_FAILED = "We couldn't refresh your session. Try again in a moment.";

function getErrorStatus(error: unknown): number | undefined {
  const record = error as { status?: unknown; statusCode?: unknown } | null;
  if (!record) {
    return undefined;
  }
  if (typeof record.status === "number") {
    return record.status;
  }
  if (typeof record.statusCode === "number") {
    return record.statusCode;
  }
  return undefined;
}

function isRetryableProfileLookupError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return status === undefined || status === 0 || status === 408 || status === 429 || status >= 500;
}

/** A safe profile-read failure that records whether an active refreshed session may retry. */
export class StaffProfileLookupError extends StaffSessionError {
  readonly retryable: boolean;

  constructor(error: unknown) {
    super("session_invalid", PROFILE_LOOKUP_FAILED);
    this.name = "StaffProfileLookupError";
    this.retryable = isRetryableProfileLookupError(error);
  }
}

function shouldRetainRefreshedToken(
  error: unknown,
  preserveOnProfileLookupFailure: boolean | undefined,
): boolean {
  return (
    Boolean(preserveOnProfileLookupFailure) &&
    error instanceof StaffProfileLookupError &&
    error.retryable
  );
}

/** User-safe denial reason that distinguishes pending / rejected / inactive / missing. */
function describeProfileDenial(profile: AuthProfile | null): string {
  if (!profile) {
    return NO_STAFF_PROFILE;
  }
  if (profile.approval_status === "rejected") {
    return ACCOUNT_REJECTED;
  }
  if (profile.is_active === false) {
    return ACCOUNT_INACTIVE;
  }
  return ACCOUNT_PENDING;
}

/** Resolve the staff profile linked to an `auth.users` id, or null when none. */
export async function resolveStaffProfileByAuthUser(
  authUserId: string,
): Promise<AuthProfile | null> {
  const response = await getBackendClient()
    .data.from("profiles")
    .select(PROFILE_COLUMNS)
    .eq("auth_user_id", authUserId)
    .maybeSingle();
  const { data, error } = response;
  if (error) {
    // The SDK returns a top-level HTTP status for data-query failures, but the
    // repository's normalized QueryResult does not retain it yet.
    const { status } = response as typeof response & { readonly status?: unknown };
    throw new StaffProfileLookupError({ status, statusCode: error.statusCode });
  }
  // Cast: `select()` keeps the full generated Row type regardless of the string
  // projection, so pin the runtime shape (PROFILE_COLUMNS) to AuthProfile.
  return (data as AuthProfile | null) ?? null;
}

/**
 * Given a freshly minted InsForge access token + auth.users id, set the token,
 * resolve the linked staff profile, and enforce the approved-active gate. On a
 * gate failure the InsForge session is cleared and a typed error is thrown. A
 * retryable, same-subject refresh-path profile lookup failure retains its newly
 * refreshed token so the active session can retry instead of being locally logged out.
 */
async function finalizePasswordSession(input: {
  readonly accessToken: string;
  readonly authUserId: string;
  readonly email?: string;
  readonly preserveAccessTokenOnProfileLookupFailure?: boolean;
  readonly isCurrent?: () => boolean;
  readonly revokeSession?: () => Promise<unknown>;
}): Promise<StaffSessionResult> {
  const isCurrent = input.isCurrent ?? (() => true);
  assertCurrent(isCurrent);
  // The profile read is RLS-gated on auth.uid(); the access token must be live.
  insforge.setAccessToken(input.accessToken);

  let profile: AuthProfile | null;
  try {
    profile = await resolveStaffProfileByAuthUser(input.authUserId);
  } catch (error) {
    assertCurrent(isCurrent);
    if (!shouldRetainRefreshedToken(error, input.preserveAccessTokenOnProfileLookupFailure)) {
      insforge.setAccessToken(null);
    }
    throw error;
  }

  assertCurrent(isCurrent);
  if (!isApprovedProfile(profile)) {
    const message = describeProfileDenial(profile);
    await (input.revokeSession?.() ?? insforge.auth.signOut()).catch(() => {
      // Best-effort: the gate already denies access regardless.
    });
    assertCurrent(isCurrent);
    insforge.setAccessToken(null);
    throw new StaffSessionError("approval_pending", message);
  }

  return {
    accessToken: input.accessToken,
    expiresAt: null,
    origin: "password",
    profile,
    user: {
      email: input.email ?? profile.email ?? undefined,
      id: input.authUserId,
      role: profile.role ?? "worker",
    },
  };
}

/** Sign in with email + password through InsForge and resolve the staff session. */
export async function signInWithPasswordSession(input: {
  readonly email: string;
  readonly password: string;
}): Promise<StaffSessionResult> {
  passwordOperations += 1;
  cancelPasswordSessionRestores();
  try {
    const { data, error } = await insforge.auth.signInWithPassword({
      email: input.email,
      password: input.password,
    });
    if (error || !data?.user?.id) {
      throw new StaffSessionError("session_invalid", SIGN_IN_FAILED);
    }
    return await finalizePasswordSession({
      accessToken: data.accessToken,
      authUserId: data.user.id,
      email: data.user.email,
    });
  } finally {
    passwordOperations -= 1;
  }
}

function isAuthDeniedStatus(status: number | undefined): boolean {
  return status === 401 || status === 403;
}

function isAbsentPasswordSessionError(error: unknown): boolean {
  const record = error as {
    message?: string;
    status?: number;
    statusCode?: number;
  } | null;
  if (!record) {
    return false;
  }
  // The SDK's InsForgeError carries `statusCode`; hand-built and older errors
  // may carry `status`. Accept both so a real "no session" 401/403 stays out
  // of the transient-failure path that surfaces session_invalid on login.
  if (isAuthDeniedStatus(record.status) || isAuthDeniedStatus(record.statusCode)) {
    return true;
  }
  const message = record.message?.toLowerCase() ?? "";
  return message.includes("cookie") || message.includes("no refresh");
}

/**
 * Restore a password session from the InsForge httpOnly refresh cookie. Returns
 * null when no password session exists (normal — the caller then tries SMS).
 * Failed profile validation is surfaced as a typed session error.
 */
export async function restorePasswordSession(
  expectedAuthUserId?: string,
  isCurrent: () => boolean = () => true,
): Promise<StaffSessionResult | null> {
  if (passwordOperations > 0) throw new PasswordSessionRestoreCancelledError();
  const controller = new AbortController();
  const current = () => !controller.signal.aborted && isCurrent();
  activeRestores.add(controller);
  const signalCleanups: (() => void)[] = [];
  try {
    const { url, anonKey } = readBackendConfig();
    // refreshSession saves tokens before returning. Keep that SDK mutation local
    // until this attempt still owns the provider's auth generation.
    const client = createClient({
      baseUrl: url,
      anonKey,
      auth: { detectOAuthCallback: false },
      fetch: async (input, init) => {
        assertCurrent(current);
        const response = await fetch(input, {
          ...init,
          signal: combineRestoreSignals(init?.signal, controller.signal, signalCleanups),
        });
        assertCurrent(current);
        return response;
      },
    });
    let refreshed: Awaited<ReturnType<typeof client.auth.refreshSession>>;
    try {
      refreshed = await client.auth.refreshSession();
    } catch (error) {
      assertCurrent(current);
      if (isAbsentPasswordSessionError(error)) return null;
      throw new StaffSessionError("session_invalid", SESSION_REFRESH_FAILED);
    }
    assertCurrent(current);
    if (refreshed.error) {
      if (isAbsentPasswordSessionError(refreshed.error)) return null;
      throw new StaffSessionError("session_invalid", SESSION_REFRESH_FAILED);
    }
    const data = refreshed.data;
    if (!data?.user) return null;
    if (expectedAuthUserId && data.user.id !== expectedAuthUserId) {
      insforge.setAccessToken(null);
      return null;
    }
    return await finalizePasswordSession({
      accessToken: data.accessToken,
      authUserId: data.user.id,
      email: data.user.email,
      preserveAccessTokenOnProfileLookupFailure: Boolean(expectedAuthUserId),
      isCurrent: current,
      revokeSession: () => client.auth.signOut(),
    });
  } finally {
    for (const cleanup of signalCleanups) cleanup();
    activeRestores.delete(controller);
  }
}

/** Clear the InsForge password session (server cookie + in-memory token). */
export async function signOutPasswordSession(): Promise<void> {
  passwordOperations += 1;
  cancelPasswordSessionRestores();
  try {
    await insforge.auth.signOut();
  } finally {
    passwordOperations -= 1;
  }
}
